import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { setApiKeyHeader } from './fspApi';

import config from 'utils/config';

interface NodeDataObject {
  nodeData: NodeData;
}

interface NodeDataAttibutes {
  language: string;
  disableHeadToHead: boolean;
  disableFormGuide: boolean;
  disableLineUps: boolean;
  disableMatchOfficials: boolean;
  disableSquadList: boolean;
}

interface NodeData {
  id: string;
  version: string;
  timestamp: string;
  foreignId: string;
  title: string;
  description: string;
  summary: string;
  sys: Record<string, unknown>;
  pubInfo: Record<string, unknown>;
  attributes: NodeDataAttibutes;
  files: {
    content: Record<string, unknown>;
    'content.json': {
      data: {
        document: {
          content: Array<ContentStructure>;
        };
      };
    };
  };
  pubInfoEx: Record<string, unknown>;
  links: NodeDataPageLinkGroup;
  contentLength: number;
  lastModified: number;
  dataType: string;
  etag: string;
  disableHeadToHead: boolean;
  disableFormGuide: boolean;
  disableLineUps: boolean;
  disableMatchOfficials: boolean;
}

type ShowVariablesOnly = Pick<
  NodeDataAttibutes,
  'disableHeadToHead' | 'disableFormGuide' | 'disableLineUps' | 'disableMatchOfficials' | 'disableSquadList'
>;

type PressKitCMSArticleQueryArgs = {
  matchId: string;
  language?: string;
  type?: string;
  competitions?: string;
  specificArticles?: string;
};

interface NodeDataPageLinkGroup {
  pagelink: {
    away: Array<Link>;
    home: Array<Link>;
    main: Array<Link>;
  };
}

interface Link {
  key: number;
  maxItems: number;
  metadata: Record<string, unknown>;
  targetId: string;
}

interface ContentStructure {
  _html: string;
  _type: string;
}

type resultType = {
  result: Array<NodeDataObject>;
  count: number;
};

type articleType = {
  model: {
    data: NodeData;
  };
};

export interface ArticleResult {
  title: string;
  content: string;
  key: string;
}

export interface ArticleFactsResult {
  title: string;
  content: string | Array<string | undefined>;
  key: string;
}

export type Article = ArticleFactsResult | ArticleResult;

const PressKitCMSArticleQueryArgsDefaults = {
  language: 'en',
  type: 'presskit',
};

const getDisableSquadList = (compId: string) => {
  const competitionConf = config.competitions.find((item) => item.id === compId) ?? config.competitionsDefault;

  return competitionConf?.disableSquadList ?? false;
};

// Define a service using a base URL and expected endpoints
export const cmsApi = createApi({
  reducerPath: 'cmsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_CMS_API_BASE_URL,
    prepareHeaders: setApiKeyHeader('cobaltapikey'),
  }),
  endpoints: (builder) => ({
    getMatchDetailsSections: builder.query<ShowVariablesOnly, PressKitCMSArticleQueryArgs>({
      query: ({ matchId, language = 'en', type = 'presskit', competitions }) => {
        return `/api/cachedsearch/build?type=${type}&param.attributes.language=${language}&param.attributes.competitions=${competitions}&param.attributes.footballEntities.match.matchId=${matchId}&aggregator=lightnodejson`;
      },
      transformResponse: (response: { count: number; result: Array<NodeDataObject> }, meta, args) => {
        let defaultResponse: ShowVariablesOnly = {
          disableHeadToHead: false,
          disableFormGuide: false,
          disableLineUps: false,
          disableMatchOfficials: false,
          disableSquadList: getDisableSquadList(args.competitions ?? ''),
        };

        if (response.count > 0) {
          const result: NodeDataAttibutes = response.result[0].nodeData.attributes;

          defaultResponse = {
            disableHeadToHead: result['disableHeadToHead'] ?? false,
            disableFormGuide: result['disableFormGuide'] ?? false,
            disableLineUps: result['disableLineUps'] ?? false,
            disableMatchOfficials: result['disableMatchOfficials'] ?? false,
            disableSquadList: result['disableSquadList'] ?? false,
          };
        }

        return defaultResponse;
      },
    }),
    getHighlightsFromCMS: builder.query<Record<string, ArticleResult>, PressKitCMSArticleQueryArgs>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        _arg = { ...PressKitCMSArticleQueryArgsDefaults, ..._arg };

        const featchLink = `/api/cachedsearch/build?type=${_arg.type}&param.attributes.language=${_arg.language}&param.attributes.competitions=${_arg.competitions}&param.attributes.footballEntities.match.matchId=${_arg.matchId}&aggregator=lightnodejson`;

        const presskitArticleReturn = await fetchWithBQ(featchLink);

        if (presskitArticleReturn.error) throw presskitArticleReturn.error;

        const result: Record<string, ArticleResult> = {};

        const presskitArticleData = presskitArticleReturn.data as resultType;

        if (presskitArticleData.count > 0) {
          const pressKitArticleDetailsReturn = await fetchWithBQ(
            `/api/pages/${presskitArticleData.result[0].nodeData.id}?aggregator=lightpagejson`,
          );

          if (pressKitArticleDetailsReturn.error) throw pressKitArticleDetailsReturn.error;

          const presskitArticleDetailsData = pressKitArticleDetailsReturn.data as articleType;

          const links: NodeDataPageLinkGroup = presskitArticleDetailsData.model.data.links;

          let updatedLinks = Object.entries(links.pagelink);

          if (_arg?.specificArticles) {
            updatedLinks = updatedLinks.filter((link) => {
              return _arg.specificArticles === link[0];
            });
          }

          for (const [key, value] of updatedLinks) {
            const targetId = value[0].targetId;

            const articleReturn = await fetchWithBQ(`/api/pages/${targetId}?aggregator=lightpagejson`);

            if (articleReturn.error) throw articleReturn.error;
            const articleDataWrapper = articleReturn.data as articleType;

            const contentListArray = articleDataWrapper.model.data.files['content.json'].data.document.content;

            let listItem = '';

            contentListArray?.forEach((item: ContentStructure) => {
              if (item._type === 'LIST' || item._type === 'PARAGRAPH') {
                listItem = `${listItem}|${item._html}`;
              }
            });

            const cont = listItem ?? '<p>No content available yet.</p>';

            result[key] = {
              title: articleDataWrapper.model.data.title,

              content: cont,
              key,
            };
          }
        }

        return { data: result };
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints

export const { useGetMatchDetailsSectionsQuery, useGetHighlightsFromCMSQuery } = cmsApi;
